import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  MenuItem,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Autocomplete,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { ArrowBack, Save, Add as AddIcon } from '@mui/icons-material';
import { PageHeader } from '../../components/common/pageKit';
import { useControlRoomColors } from '../../components/common/controlRoom';
import { productService } from '../../services/productService';
import { categoryService, Category } from '../../services/categoryService';
import { hsnService, HsnCode } from '../../services/hsnService';

// ---------------------------------------------------------------------------
// Create / edit a Product as a dedicated full page (not a modal), mirroring
// QuotationFormPage / SalesContractFormPage: titled sections and a sticky
// bottom action bar. The create/update apiClient calls and payload shape are
// unchanged from the old dialog, so the backend is untouched.
// ---------------------------------------------------------------------------

const UNIT_OPTIONS = ['Nos', 'Kg', 'Ltr', 'Mtr', 'Sqm', 'Pcs', 'Box', 'Set', 'Hours', 'License', 'Project'];
const GST_RATE_OPTIONS = [0, 5, 12, 18, 28];

interface ProductFormData {
  product_name: string;
  description: string;
  hsn_code: string;
  unit_of_measure: string;
  category: string;
  base_price: number;
  purchase_price: number;
  gst_rate: number;
  is_active: boolean;
}

const emptyForm = (): ProductFormData => ({
  product_name: '',
  description: '',
  hsn_code: '',
  unit_of_measure: 'Nos',
  category: '',
  base_price: 0,
  purchase_price: 0,
  gst_rate: 18,
  is_active: true,
});

const ProductFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const c = useControlRoomColors();

  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingRefData, setLoadingRefData] = useState(true);
  const [loadingProduct, setLoadingProduct] = useState(isEdit);

  const [formData, setFormData] = useState<ProductFormData>(emptyForm());
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // HSN autocomplete state
  const [hsnOptions, setHsnOptions] = useState<HsnCode[]>([]);
  const [hsnLoading, setHsnLoading] = useState(false);
  const [hsnInputValue, setHsnInputValue] = useState('');
  const [selectedHsn, setSelectedHsn] = useState<HsnCode | null>(null);

  // Add Category dialog
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [categoryError, setCategoryError] = useState('');

  // Add HSN dialog
  const [openHsnDialog, setOpenHsnDialog] = useState(false);
  const [newHsnCode, setNewHsnCode] = useState('');
  const [newHsnDescription, setNewHsnDescription] = useState('');
  const [newHsnGstRate, setNewHsnGstRate] = useState(18);
  const [hsnError, setHsnError] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchCategories = async () => {
    try {
      const response = await categoryService.getCategories();
      setCategories(response.categories);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const fetchHsnCodes = async () => {
    setHsnLoading(true);
    try {
      const response = await hsnService.getAll({ limit: 50 });
      setHsnOptions(response.hsnCodes);
    } catch (err) {
      console.error('Failed to fetch HSN codes:', err);
    } finally {
      setHsnLoading(false);
    }
  };

  useEffect(() => {
    setLoadingRefData(true);
    Promise.all([fetchCategories(), fetchHsnCodes()]).finally(() => setLoadingRefData(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoadingProduct(true);
    productService
      .getProduct(id)
      .then(({ product }) => {
        setFormData({
          product_name: product.product_name,
          description: product.description || '',
          hsn_code: product.hsn_code || '',
          unit_of_measure: product.unit_of_measure || 'Nos',
          category: product.category || '',
          base_price: product.base_price || 0,
          purchase_price: product.purchase_price || 0,
          gst_rate: product.gst_rate || 18,
          is_active: product.is_active,
        });
        if (product.hsn_code) {
          setSelectedHsn({ id: 0, hsn_code: product.hsn_code, description: '', gst_rate: product.gst_rate || 18 });
          setHsnInputValue(product.hsn_code);
        }
      })
      .catch((err: any) => setError(err.message || 'Failed to load product'))
      .finally(() => setLoadingProduct(false));
  }, [isEdit, id]);

  // HSN search with debounce
  useEffect(() => {
    if (hsnInputValue.length < 2) return;

    const searchHsn = async () => {
      setHsnLoading(true);
      try {
        const response = await hsnService.search(hsnInputValue);
        setHsnOptions(response.hsnCodes);
      } catch (err) {
        console.error('Failed to search HSN codes:', err);
      } finally {
        setHsnLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchHsn, 300);
    return () => clearTimeout(debounceTimer);
  }, [hsnInputValue]);

  const handleHsnSelect = (hsn: HsnCode | null) => {
    setSelectedHsn(hsn);
    if (hsn && hsn.hsn_code !== '__ADD_NEW__') {
      const gstRate = Number(hsn.gst_rate) || 18;
      setFormData(prev => ({ ...prev, hsn_code: hsn.hsn_code, gst_rate: gstRate }));
    } else if (!hsn) {
      setFormData(prev => ({ ...prev, hsn_code: '' }));
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      setCategoryError('Category name is required');
      return;
    }
    if (newCategoryName.trim().length < 2) {
      setCategoryError('Category name must be at least 2 characters');
      return;
    }

    try {
      const response = await categoryService.createCategory(newCategoryName.trim(), newCategoryDescription.trim() || undefined);
      await fetchCategories();
      setFormData(prev => ({ ...prev, category: response.category.name }));
      setOpenCategoryDialog(false);
      setNewCategoryName('');
      setNewCategoryDescription('');
      setCategoryError('');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create category';
      setCategoryError(errorMessage);
    }
  };

  const handleCreateHsn = async () => {
    if (!newHsnCode.trim()) {
      setHsnError('HSN code is required');
      return;
    }
    if (!/^\d{4,8}$/.test(newHsnCode.trim())) {
      setHsnError('HSN code must be 4-8 digits');
      return;
    }
    if (!newHsnDescription.trim()) {
      setHsnError('Description is required');
      return;
    }

    try {
      const response = await hsnService.create({
        hsn_code: newHsnCode.trim(),
        description: newHsnDescription.trim(),
        gst_rate: newHsnGstRate
      });

      const newHsn = response.hsnCode;
      setSelectedHsn(newHsn);
      setFormData(prev => ({ ...prev, hsn_code: newHsn.hsn_code, gst_rate: Number(newHsn.gst_rate) || 18 }));

      setOpenHsnDialog(false);
      setNewHsnCode('');
      setNewHsnDescription('');
      setNewHsnGstRate(18);
      setHsnError('');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create HSN code';
      setHsnError(errorMessage);
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.product_name.trim()) {
      errors.product_name = 'Product name is required';
    } else if (formData.product_name.trim().length < 2) {
      errors.product_name = 'Product name must be at least 2 characters';
    }

    if (formData.hsn_code && !/^\d{4,8}$/.test(formData.hsn_code)) {
      errors.hsn_code = 'HSN code must be 4-8 digits';
    }

    if (formData.base_price < 0) {
      errors.base_price = 'Base price cannot be negative';
    }

    if (formData.purchase_price < 0) {
      errors.purchase_price = 'Purchase price cannot be negative';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && id) {
        await productService.updateProduct(id, formData);
      } else {
        await productService.createProduct(formData);
      }
      navigate('/sales/products');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save product');
    } finally {
      setSubmitting(false);
    }
  };

  const loading = loadingRefData || loadingProduct;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 10 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/sales/products')} sx={{ mb: 1 }}>
        Back to Products
      </Button>

      <PageHeader
        title={isEdit ? 'Edit Product' : 'Add New Product'}
        subtitle="Maintain the product catalogue, HSN mapping and GST rates."
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Section: Basic Details */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Basic Details
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Product Name"
              value={formData.product_name}
              onChange={(e) => setFormData(prev => ({ ...prev, product_name: e.target.value }))}
              error={!!formErrors.product_name}
              helperText={formErrors.product_name}
              required
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              multiline
              minRows={2}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Section: Classification */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Classification
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '__ADD_NEW__') {
                    setOpenCategoryDialog(true);
                  } else {
                    setFormData(prev => ({ ...prev, category: value }));
                  }
                }}
                label="Category"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {categories.map(category => (
                  <MenuItem key={category.id} value={category.name}>{category.name}</MenuItem>
                ))}
                <MenuItem value="__ADD_NEW__" sx={{ borderTop: '1px solid', borderColor: 'divider', color: 'primary.main', fontWeight: 'medium' }}>
                  <AddIcon sx={{ mr: 1, fontSize: 20 }} /> Add New Category
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Unit of Measure</InputLabel>
              <Select
                value={formData.unit_of_measure}
                onChange={(e) => setFormData(prev => ({ ...prev, unit_of_measure: e.target.value }))}
                label="Unit of Measure"
              >
                {UNIT_OPTIONS.map(unit => (
                  <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Section: Pricing */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Pricing
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Base Price (Selling Price)"
              type="number"
              value={formData.base_price}
              onChange={(e) => setFormData(prev => ({ ...prev, base_price: parseFloat(e.target.value) || 0 }))}
              error={!!formErrors.base_price}
              helperText={formErrors.base_price}
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Purchase Price (Cost Price)"
              type="number"
              value={formData.purchase_price}
              onChange={(e) => setFormData(prev => ({ ...prev, purchase_price: parseFloat(e.target.value) || 0 }))}
              error={!!formErrors.purchase_price}
              helperText={formErrors.purchase_price}
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Section: Tax (HSN, GST) */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Tax (HSN, GST)
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Autocomplete
              size="small"
              value={selectedHsn}
              onChange={(_, newValue) => {
                if (newValue && newValue.id === -1) {
                  setOpenHsnDialog(true);
                } else {
                  handleHsnSelect(newValue);
                }
              }}
              inputValue={hsnInputValue}
              onInputChange={(_, newInputValue) => setHsnInputValue(newInputValue)}
              options={[
                ...hsnOptions,
                { id: -1, hsn_code: '__ADD_NEW__', description: 'Add New HSN Code', gst_rate: 0 } as HsnCode
              ]}
              getOptionLabel={(option) => {
                if (option.hsn_code === '__ADD_NEW__') return '';
                return option.hsn_code ? `${option.hsn_code} - ${option.description}` : '';
              }}
              filterOptions={(options) => options}
              loading={hsnLoading}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option) => {
                if (option.hsn_code === '__ADD_NEW__') {
                  return (
                    <li {...props} key="add-new" style={{ borderTop: '1px solid #e0e0e0', color: '#1976d2', fontWeight: 500 }}>
                      <AddIcon sx={{ mr: 1, fontSize: 20 }} /> Add New HSN Code
                    </li>
                  );
                }
                return (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">{option.hsn_code}</Typography>
                      <Typography variant="caption" color="text.secondary">{option.description} ({option.gst_rate}%)</Typography>
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="HSN Code"
                  placeholder="Search HSN code..."
                  error={!!formErrors.hsn_code}
                  helperText={formErrors.hsn_code || 'Type to search HSN/SAC codes'}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {hsnLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              selectOnFocus
              clearOnBlur={false}
              handleHomeEndKeys
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel>GST Rate</InputLabel>
              <Select
                value={formData.gst_rate}
                onChange={(e) => setFormData(prev => ({ ...prev, gst_rate: Number(e.target.value) }))}
                label="GST Rate"
              >
                {GST_RATE_OPTIONS.map(rate => (
                  <MenuItem key={rate} value={rate}>{rate}%</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Section: Inventory flags */}
      {isEdit && (
        <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
            Inventory
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
              />
            }
            label="Active"
          />
        </Paper>
      )}

      {/* Sticky bottom action bar */}
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 5,
          mt: 1,
          py: 1.5,
          px: 2,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: c.panel,
        }}
      >
        <Button onClick={() => navigate('/sales/products')} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" startIcon={<Save />} onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Product'}
        </Button>
      </Paper>

      {/* Add Category Dialog */}
      <Dialog
        open={openCategoryDialog}
        onClose={() => {
          setOpenCategoryDialog(false);
          setNewCategoryName('');
          setNewCategoryDescription('');
          setCategoryError('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Category</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Category Name"
                value={newCategoryName}
                onChange={(e) => {
                  setNewCategoryName(e.target.value);
                  setCategoryError('');
                }}
                error={!!categoryError}
                helperText={categoryError}
                required
                autoFocus
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Description (optional)"
                value={newCategoryDescription}
                onChange={(e) => setNewCategoryDescription(e.target.value)}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => {
            setOpenCategoryDialog(false);
            setNewCategoryName('');
            setNewCategoryDescription('');
            setCategoryError('');
          }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateCategory}>
            Create Category
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add HSN Dialog */}
      <Dialog
        open={openHsnDialog}
        onClose={() => {
          setOpenHsnDialog(false);
          setNewHsnCode('');
          setNewHsnDescription('');
          setNewHsnGstRate(18);
          setHsnError('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New HSN Code</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="HSN/SAC Code"
                value={newHsnCode}
                onChange={(e) => {
                  setNewHsnCode(e.target.value);
                  setHsnError('');
                }}
                error={!!hsnError && hsnError.includes('HSN')}
                helperText="4-8 digit code"
                required
                autoFocus
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>GST Rate</InputLabel>
                <Select
                  value={newHsnGstRate}
                  onChange={(e) => setNewHsnGstRate(Number(e.target.value))}
                  label="GST Rate"
                >
                  {GST_RATE_OPTIONS.map(rate => (
                    <MenuItem key={rate} value={rate}>{rate}%</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Description"
                value={newHsnDescription}
                onChange={(e) => {
                  setNewHsnDescription(e.target.value);
                  setHsnError('');
                }}
                error={!!hsnError && hsnError.includes('Description')}
                required
                multiline
                rows={2}
              />
            </Grid>
            {hsnError && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="error">{hsnError}</Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => {
            setOpenHsnDialog(false);
            setNewHsnCode('');
            setNewHsnDescription('');
            setNewHsnGstRate(18);
            setHsnError('');
          }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateHsn}>
            Create HSN Code
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductFormPage;
